// Wallet Service - Phase 11+ Engine-based (NO legacy requests)
// Uses canonical portal wallet endpoints with idempotency
import apiClient, { getLastCorrelationId } from "@/api/client";
import { IdemKeys } from "../api/idempotency";
import api from "./api";

// Re-export correlation ID getter for error handling
export { getLastCorrelationId };

// ========================================
// GATEWAY DEPOSIT (1TapPay/7AI Pay)
// ========================================

// Create gateway deposit
export async function createGatewayDeposit({
    amount,
    method,
    currency = "USD",
}) {
    const idempotencyKey = `gateway-deposit-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

    try {
        const response = await api.post(
            "/gateway/deposit",
            {
                amount,
                method,
                currency,
            },
            {
                headers: {
                    "X-Idempotency-Key": idempotencyKey,
                },
            },
        );

        // Extract data from axios response
        // Backend returns: {success: true, order_id: "...", pay_url: "...", original_amount, charged_amount, polling: {...}}
        const data = response.data || response;

        return data;
    } catch (error) {
        // Enhanced error handling with correlation ID
        const correlationId = error.correlationId || getLastCorrelationId();
        const errorMessage =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.message ||
            "Gateway deposit failed";

        console.error("[createGatewayDeposit] Error:", errorMessage, {
            correlationId,
        });

        return {
            success: false,
            error: errorMessage,
            correlationId,
        };
    }
}

// Check gateway deposit status (active polling - triggers auto-update)
export async function checkGatewayDepositStatus(depositId) {
    try {
        const response = await api.post(`/deposits/${depositId}/poll-status`);
        const data = response.data || response;
        return { success: true, data };
    } catch (error) {
        console.error("[checkGatewayDepositStatus] Error:", error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message,
        };
    }
}

// ========================================
// LEGACY DEPOSIT/WITHDRAWAL
// ========================================

export const getWalletBalance = async () => {
    const axiosResponse = await api.get("/wallet/balance");
    const data = axiosResponse.data || axiosResponse;

    // Transform snake_case API response to camelCase for frontend consistency
    return {
        balance: data.balance ?? 0,
        playCredits: data.play_credits ?? 0,
        pending: data.pending ?? 0,
        lockedCommission: data.locked_commission ?? 0,
        total: data.total ?? 0,
    };
};

const toPublicUrl = (u) => {
    if (!u) return u;
    if (
        typeof u === "string" &&
        (u.startsWith("http://") || u.startsWith("https://"))
    )
        return u;
    if (typeof u === "string" && u.startsWith("/")) {
        // Rewrite legacy `/uploads/...` paths to `/api/uploads/...` so the
        // ingress routes them to the backend instead of the frontend
        // container (the static-mount path is NOT exposed externally).
        const path = u.startsWith("/uploads/") ? `/api${u}` : u;
        return `${process.env.REACT_APP_BACKEND_URL}${path}`;
    }
    return u;
};

export const getPaymentMethods = async (flow) => {
    // PHASE 2.1 FIX: Use canonical endpoint that returns id + full config
    // Client endpoint: /api/payment-methods?flow=deposit|withdraw
    const axiosResponse = await api.get(`/payment-methods?flow=${flow}`);
    const response = axiosResponse.data || axiosResponse;
    // Backend returns "methods" array with id + all canonical fields
    const methods = response.methods || response.payment_methods || [];

    // Map backend response fields to frontend expectations
    const iconMap = {
        cashapp: "dollar",
        chime: "building",
        paypal: "at",
        visa: "card",
        mastercard: "card",
        bitcoin: "bitcoin",
        crypto: "bitcoin",
        zelle: "send",
        venmo: "dollar",
        bank: "building",
        others: "dollar",
    };

    return methods.map((m) => {
        // PHASE 1B FIX: Use nullish coalescing (??) not OR (||) for numeric fields
        const bonusOrFee = m.bonus_or_fee ?? m.bonus_percent ?? 0;
        const direction = m.bonus_fee_direction || "bonus";
        const description = m.bonus_fee_description || "";

        let depositBonus = "None";
        let withdrawFee = "Free";

        if (bonusOrFee > 0) {
            if (direction === "bonus") {
                depositBonus = description || `+${bonusOrFee}%`;
            } else if (direction === "fee") {
                withdrawFee = description || `${bonusOrFee}% fee`;
            }
        }

        // PHASE 1B FIX: Use backend payout_fields exactly as configured
        // Only apply legacy fallback when backend provides NO payout_fields at all
        // GLOBAL DEFAULT: every withdraw method must show at least one
        // required payout-destination field so the user always tells us
        // where to send the money. The default is editable but cannot be
        // skipped. If the admin/vendor configured custom fields, we keep
        // those — but still ensure at least one is `required:true`.
        const DEFAULT_PAYOUT_FIELD = {
            name: "payout_destination",
            label: "Payout Destination",
            placeholder:
                "CashTag / Account / Phone / Email / Wallet — where to send the money",
            required: true,
        };

        let payoutFields;
        let payoutFieldsLegacyFallback = false;

        if (Array.isArray(m.payout_fields) && m.payout_fields.length > 0) {
            // Backend explicitly provided payout_fields (non-empty)
            const cloned = m.payout_fields.map((f) => ({ ...f }));
            // Force at least one required field – upgrade the first one
            // to required if no required field exists yet.
            const hasRequired = cloned.some((f) => f.required === true);
            if (!hasRequired && cloned.length > 0) {
                cloned[0].required = true;
            }
            payoutFields = cloned;
        } else if (flow === "withdraw") {
            // Backend gave [] or omitted payout_fields entirely → use the
            // global default field so the user is forced to enter a
            // destination.
            payoutFields = [{ ...DEFAULT_PAYOUT_FIELD }];
            payoutFieldsLegacyFallback = true;
        }

        return {
            id: m.id,
            name:
                m.title ||
                m.display_name ||
                m.name ||
                m.method ||
                m.id
                    ?.replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase()) ||
                "Unknown",
            method: m.method || m.id?.split("_")[0] || "", // Derive method from id if null
            accountNumber: m.method_details || m.account_info || "",
            icon: iconMap[m.method] || iconMap[m.type] || m.icon || "dollar",
            depositBonus,
            withdrawFee,
            bonusOrFee,
            bonusFeeDirection: direction,
            bonusFeeDescription: description,
            qrCode: toPublicUrl(m.qr_code_url || m.qr_image_url),
            note: m.instructions || "",
            processingTime: "5-30 minutes",
            // PHASE 1B FIX: Use nullish coalescing for numeric fields
            minAmount: m.min_amount ?? 0,
            maxAmount: m.max_amount ?? 10000,
            allowCustomAmount: m.allow_custom_amount ?? true,
            presetAmounts: m.preset_amounts,
            payoutFields,
            payoutFieldsLegacyFallback, // Flag for debugging
            // Part 5: Gateway fields
            payment_mode: m.payment_mode || "LOCAL_MANUAL",
            gateway_provider: m.gateway_provider,
            gateway_method: m.gateway_method,
        };
    });
};

/**
 * Create deposit using engine-based portal wallet endpoint
 *
 * Phase 11+: NO legacy /api/requests/deposit
 * Uses: POST /api/portal/wallet/deposit
 *
 * @param {Object} params
 * @param {number} params.amount - Deposit amount
 * @param {string} params.methodId - Payment method ID
 * @param {File} params.proofImage - Optional proof image (for manual proof upload after order creation)
 * @param {string} params.notes - Optional notes
 * @param {string} params.mode - Payment mode: LOCAL_MANUAL or GATEWAY
 * @param {string} params.userId - User ID for idempotency key
 * @returns {Promise} - Order + payment details
 */
export const createDeposit = async ({
    amount,
    methodId,
    mode = "LOCAL_MANUAL",
    userId,
    notes,
}) => {
    // Generate deterministic idempotency key
    const idemKey = IdemKeys.depositCreate(userId, amount, methodId, mode);

    const response = await api.post(
        "/portal/wallet/deposit",
        {
            amount: parseFloat(amount),
            currency: "USD",
            method_id: methodId,
            mode: mode,
        },
        {
            headers: { "X-Idempotency-Key": idemKey },
        },
    );

    return response.data || response;
};

/**
 * Submit proof for manual deposit payment
 *
 * After creating deposit, user uploads proof via this endpoint.
 *
 * @param {string} paymentId - Payment ID from deposit creation
 * @param {File} proofImage - Proof image file
 * @param {string} proofHash - Hash of proof for idempotency
 * @returns {Promise} - Updated payment status
 */
export const submitDepositProof = async (paymentId, proofImage, proofHash) => {
    // Upload proof image first
    const formData = new FormData();
    formData.append("file", proofImage);
    formData.append("upload_type", "proof");

    // Use apiClient for upload (has auth interceptor)
    // CACHE BUSTER: Add timestamp to force new request
    const cacheBuster = `?v=${Date.now()}`;

    const uploadConfig = {
        headers: {
            "Content-Type": "multipart/form-data",
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
        },
    };
    const uploadResponse = await api.post(
        `/uploads${cacheBuster}`,
        formData,
        uploadConfig,
    );
    const upload = uploadResponse.data || uploadResponse;
    const proofUrl = upload.url;

    // Submit proof to portal wallet engine endpoint
    const idemKey = IdemKeys.submitProof(
        paymentId,
        proofHash || Date.now().toString(),
    );

    const axiosResponse = await api.post(
        `/portal/wallet/deposit/${paymentId}/proof`,
        {
            proof_url: proofUrl,
            proof_hash: proofHash || Date.now().toString(),
        },
        {
            headers: { "X-Idempotency-Key": idemKey },
        },
    );
    return axiosResponse.data || axiosResponse;
};

/**
 * Create withdraw using engine-based portal wallet endpoint
 *
 * Phase 11+: NO legacy /api/requests/withdraw
 * Uses: POST /api/portal/wallet/withdraw
 *
 * Wallet is debited immediately. Payout goes to Telegram for review.
 *
 * @param {Object} params
 * @param {number} params.amount - Withdraw amount
 * @param {string} params.methodId - Payment method ID (optional)
 * @param {Object} params.destination - Payout destination details
 * @param {string} params.userId - User ID for idempotency key
 * @returns {Promise} - Order + payout details
 */
/**
 * Create withdrawal using portal wallet endpoint
 * POST /api/portal/wallet/withdraw
 *
 * @param {Object} params
 * @param {number} params.amount - Withdrawal amount
 * @param {string} params.methodId - Payment method ID
 * @param {Object} params.destination - Payout details (account info, notes)
 * @param {File} params.proofImage - Optional proof image
 * @param {string} params.userId - User ID for idempotency key
 * @returns {Promise} - Order + payout details
 */
export const createWithdrawal = async ({
    amount,
    methodId,
    destination,
    proofImage,
    userId,
}) => {
    // Generate deterministic idempotency key
    const idemKey = IdemKeys.withdrawCreate(userId, amount, destination);

    // If proof image provided, upload it first
    let proofUrl = null;
    if (proofImage) {
        try {
            const formData = new FormData();
            formData.append("file", proofImage);
            formData.append("upload_type", "proof"); // FIX: Add upload_type as form data

            // CACHE BUSTER: Add timestamp to force new request
            const cacheBuster = `?v=${Date.now()}`;

            const uploadResp = await api.post(
                `/uploads${cacheBuster}`,
                formData,
                {
                    // FIX: Correct endpoint with cache buster
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        Pragma: "no-cache",
                    },
                },
            );
            proofUrl = uploadResp.data?.url || uploadResp.url;

            // FIX: Use absolute URL for Telegram to access the image
            if (proofUrl && !proofUrl.startsWith("http")) {
                proofUrl = `${window.location.origin}${proofUrl}`;
            }

            console.log("Proof uploaded (absolute URL):", proofUrl);
        } catch (uploadError) {
            console.error("Failed to upload withdrawal proof:", uploadError);
            // Continue without proof URL
        }
    }

    const response = await api.post(
        "/portal/wallet/withdraw",
        {
            amount: parseFloat(amount),
            currency: "USD",
            destination: destination,
            method_id: methodId,
            proof_url: proofUrl, // Include proof URL if uploaded
        },
        {
            headers: { "X-Idempotency-Key": idemKey },
        },
    );

    return response.data || response;
};

/**
 * Get wallet transaction history (ledger entries) - NORMALIZED
 * GET /api/wallet/transactions
 *
 * @param {Object} options - { limit, type }
 * @returns {Promise<Object>} { items, next_cursor }
 */
export const getTransactionHistory = async ({
    limit = 20,
    type = null,
} = {}) => {
    try {
        const params = new URLSearchParams();
        if (limit) params.append("limit", limit);
        if (type && type !== "all") params.append("type", type);

        const url = `/wallet/transactions${
            params.toString() ? "?" + params.toString() : ""
        }`;
        const response = await apiClient.get(url);
        const data = response.data || response;

        // Backend returns { transactions: [...] }
        const rawTxs = data.transactions || [];

        // Normalize each transaction
        const items = rawTxs.map(normalizeTransaction);

        return {
            success: true,
            items,
            next_cursor: null, // This endpoint doesn't support cursor yet
        };
    } catch (error) {
        return {
            success: false,
            items: [],
            next_cursor: null,
            error: error.response?.data?.detail || error.message,
            correlationId: error.correlationId || getLastCorrelationId(),
        };
    }
};

/**
 * Get paginated transaction history
 * GET /api/wallet/transactions-history
 *
 * @param {Object} options - { page, limit, txType }
 * @returns {Promise<Object>} { items, pagination, type_summary }
 */
export const getTransactionHistoryPaginated = async ({
    page = 1,
    limit = 20,
    txType = null,
} = {}) => {
    try {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (txType && txType !== "all") params.append("tx_type", txType);

        const response = await apiClient.get(
            `/wallet/transactions-history?${params.toString()}`,
        );
        const data = response.data || response;

        // Normalize transactions
        const rawTxs = data.transactions || [];
        const items = rawTxs.map(normalizeTransaction);

        return {
            success: true,
            items,
            pagination: data.pagination || {
                page: 1,
                total_pages: 1,
                total: 0,
            },
            type_summary: data.type_summary || {},
        };
    } catch (error) {
        return {
            success: false,
            items: [],
            pagination: { page: 1, total_pages: 1, total: 0 },
            type_summary: {},
            error: error.response?.data?.detail || error.message,
            correlationId: error.correlationId || getLastCorrelationId(),
        };
    }
};

/**
 * Normalize a single transaction to standard shape
 */
function normalizeTransaction(tx) {
    // Use backend direction if available, otherwise calculate
    const creditTypes = [
        "deposit",
        "game_redeem",
        "referral_commission",
        "commission_unlock",
        "bonus",
        "promo",
        "compensation",
        "achievement",
    ];
    const debitTypes = ["withdrawal", "game_load"];
    const type = tx.type || "unknown";
    const amount = tx.amount ?? 0;

    // Prefer backend direction, fallback to calculation
    let direction = tx.direction;
    if (!direction) {
        if (debitTypes.includes(type)) {
            direction = "debit";
        } else if (creditTypes.includes(type) || amount > 0) {
            direction = "credit";
        } else {
            direction = "debit";
        }
    }

    // Build normalized transaction - put spread first so our calculated fields override raw data
    return {
        // Keep raw data for backward compatibility (but allow override)
        ...tx,
        // Override with normalized fields
        id: tx.id || tx._id,
        ts: tx.created_at || tx.timestamp || new Date().toISOString(),
        type,
        direction, // Our calculated direction takes precedence
        amount: Math.abs(amount),
        currency: tx.currency || "USD",
        balance_after: tx.balance_after ?? tx.running_balance ?? null,
        ref_order_id:
            tx.order_id || tx.ref_order_id || tx.source_order_id || null,
        status: tx.status || null,
        meta: {
            source: tx.source || null,
            game_account_id: tx.game_account_id || null,
            bonus_amount: tx.bonus_amount || 0,
            bonus_type: tx.bonus_type || null,
            voided_amount: tx.voided_amount || 0,
            from_username: tx.from_username || null,
            unlock_date: tx.unlock_date || tx.lock_until || null,
        },
    };
}

/**
 * Get order status (for deposit/withdraw polling)
 *
 * @param {string} orderId - Order ID
 * @returns {Promise} - Order details with status
 */
export const getOrderStatus = async (orderId) => {
    const axiosResponse = await api.get(`/orders/${orderId}`);
    return axiosResponse.data || axiosResponse;
};

export const getWithdrawDailyLimit = async () => {
    const res = await api.get("/wallet/withdraw/daily-limit");
    return res?.data || null;
};

export default {
    getWalletBalance,
    getPaymentMethods,
    createDeposit,
    submitDepositProof,
    createWithdrawal,
    getOrderStatus,
    getTransactionHistory,
    getTransactionHistoryPaginated,
    getWithdrawDailyLimit,
    getLastCorrelationId,
};
